/**
 * Custom Facets - Collection and Availability Filtering
 * Standalone filtering system that works independently from Shopify's native facets.js
 */

export function initCustomFilters(config) {
  const { sectionId, collectionHandle } = config;
  
  let productData = {};
  let dataLoaded = false;
  let visibleProductCount = 0;
  
  // Initialize event listeners
  initializeFilters();
  
  // Force clean state on load
  clearAllFilters();
  
  // Show loading state
  showLoadingState();
  
  // Fetch products from alternate template
  const fetchUrl = `/collections/${collectionHandle}?view=output`;
  
  fetch(fetchUrl)
    .then(res => res.json())
    .then(data => {
      data.products.forEach(product => {
        productData[product.id] = {
          collections: product.collections,
          available: product.available
        };
      });
      
      dataLoaded = true;
      hideLoadingState();
      
      // Start polling to detect when Shopify lazy-loads remaining products
      let lastProductCount = document.querySelectorAll('[data-product-id]').length;
      const pollInterval = setInterval(() => {
        const currentCount = document.querySelectorAll('[data-product-id]').length;
        if (currentCount !== lastProductCount) {
          lastProductCount = currentCount;
          
          // Re-apply filters immediately
          const hasActiveFilters = document.querySelectorAll('.checkbox__input.custom-collection-checkbox:checked, .checkbox__input.custom-availability-checkbox:checked').length > 0;
          if (hasActiveFilters) {
            filterProducts();
          }
        }
        
        // Stop polling after all products loaded
        const uniqueCount = new Set(Array.from(document.querySelectorAll('[data-product-id]')).map(c => c.dataset.productId)).size;
        if (uniqueCount >= Object.keys(productData).length) {
          clearInterval(pollInterval);
        }
      }, 100);
      
      // Failsafe: stop polling after 10 seconds
      setTimeout(() => clearInterval(pollInterval), 10000);
    })
    .catch(err => {
      console.error('Failed to load product data:', err);
      dataLoaded = true;
      hideLoadingState();
    });
  
  function showLoadingState() {
    const allCheckboxes = document.querySelectorAll('.checkbox__input.custom-collection-checkbox, .checkbox__input.custom-availability-checkbox');
    allCheckboxes.forEach(cb => {
      cb.parentElement.style.opacity = '0.5';
      cb.parentElement.style.cursor = 'wait';
    });
  }
  
  function hideLoadingState() {
    const allCheckboxes = document.querySelectorAll('.checkbox__input.custom-collection-checkbox, .checkbox__input.custom-availability-checkbox');
    allCheckboxes.forEach(cb => {
      cb.parentElement.style.opacity = '1';
      cb.parentElement.style.cursor = 'pointer';
    });
  }
  
  function updateFilterUI() {
    const allCheckboxes = document.querySelectorAll('.checkbox__input.custom-collection-checkbox, .checkbox__input.custom-availability-checkbox');
    const hasActiveFilters = Array.from(allCheckboxes).some(cb => cb.checked);
    
    const clearAllBtn = document.getElementById(`custom-clear-all-${sectionId}`);
    const mobileActions = document.getElementById(`custom-mobile-actions-${sectionId}`);
    
    if (clearAllBtn) {
      clearAllBtn.style.display = hasActiveFilters ? 'block' : 'none';
    }
    if (mobileActions) {
      mobileActions.style.display = hasActiveFilters ? 'block' : 'none';
    }
  }
  
  function clearAllFilters() {
    const allCheckboxes = document.querySelectorAll('.checkbox__input.custom-collection-checkbox, .checkbox__input.custom-availability-checkbox');
    
    allCheckboxes.forEach(cb => {
      cb.checked = false;
    });
    
    if (dataLoaded) {
      filterProducts();
      updateFilterUI();
    }
  }
  
  function filterProducts() {
    if (!dataLoaded) {
      return;
    }
    
    const collectionCheckboxes = document.querySelectorAll('.checkbox__input.custom-collection-checkbox');
    const availabilityCheckboxes = document.querySelectorAll('.checkbox__input.custom-availability-checkbox');
    
    const selectedCollections = Array.from(collectionCheckboxes).filter(c => c.checked).map(c => c.value);
    const selectedAvailability = Array.from(availabilityCheckboxes).filter(c => c.checked).map(c => c.value);
    
    // Count products from productData for accurate total
    let totalVisibleProducts = 0;
    
    Object.keys(productData).forEach(productId => {
      const data = productData[productId];
      
      let passesCollectionFilter = true;
      let passesAvailabilityFilter = true;
      
      if (selectedCollections.length > 0) {
        passesCollectionFilter = selectedCollections.some(handle => data.collections.includes(handle));
      }
      
      if (selectedAvailability.length > 0) {
        const isInStock = selectedAvailability.includes('in-stock');
        const isOutOfStock = selectedAvailability.includes('out-of-stock');
        
        if (isInStock && isOutOfStock) {
          passesAvailabilityFilter = true;
        } else if (isInStock) {
          passesAvailabilityFilter = data.available;
        } else if (isOutOfStock) {
          passesAvailabilityFilter = !data.available;
        }
      }
      
      if (passesCollectionFilter && passesAvailabilityFilter) {
        totalVisibleProducts++;
      }
    });
    
    // Apply visibility to DOM cards
    const allProductCards = document.querySelectorAll('[data-product-id]');
    
    // Group cards by product ID (to handle desktop/mobile/drawer duplicates)
    const cardsByProductId = {};
    
    allProductCards.forEach(card => {
      const id = card.dataset.productId;
      if (!cardsByProductId[id]) {
        cardsByProductId[id] = [];
      }
      cardsByProductId[id].push(card);
    });
    
    // Apply visibility to cards
    Object.keys(cardsByProductId).forEach(productId => {
      const cards = cardsByProductId[productId];
      const data = productData[productId];
      
      if (!data) {
        const shouldShow = selectedCollections.length === 0 && selectedAvailability.length === 0;
        cards.forEach(card => {
          if (shouldShow) {
            card.classList.remove('custom-filtered-hidden');
          } else {
            card.classList.add('custom-filtered-hidden');
          }
        });
        return;
      }
      
      let passesCollectionFilter = true;
      let passesAvailabilityFilter = true;
      
      if (selectedCollections.length > 0) {
        passesCollectionFilter = selectedCollections.some(handle => data.collections.includes(handle));
      }
      
      if (selectedAvailability.length > 0) {
        const isInStock = selectedAvailability.includes('in-stock');
        const isOutOfStock = selectedAvailability.includes('out-of-stock');
        
        if (isInStock && isOutOfStock) {
          passesAvailabilityFilter = true;
        } else if (isInStock) {
          passesAvailabilityFilter = data.available;
        } else if (isOutOfStock) {
          passesAvailabilityFilter = !data.available;
        }
      }
      
      const isVisible = passesCollectionFilter && passesAvailabilityFilter;
      
      // Apply visibility to ALL duplicate cards using CSS class
      cards.forEach(card => {
        if (isVisible) {
          card.classList.remove('custom-filtered-hidden');
        } else {
          card.classList.add('custom-filtered-hidden');
        }
      });
    });
    
    visibleProductCount = totalVisibleProducts;
    
    // Delay counter update to prevent flickering during polling
    setTimeout(() => updateProductCount(), 150);
    updateFilterUI();
  }
  
  function updateProductCount() {
    const counter = document.querySelector('.facets__summary-count');
    if (counter) {
      counter.textContent = visibleProductCount;
    }
  }
  
  function initializeFilters() {
    const firstClickDone = new Set();
    
    // Handle AJAX sorting using Shopify Section Rendering
    function initSorting() {
      const sortSelects = document.querySelectorAll('[name="sort_by"]');
      sortSelects.forEach(select => {
        if (select.dataset.customSortingAdded) return;
        
        select.addEventListener('change', async function() {
          const sortValue = this.value;
          
          // Clear filters before sorting to avoid conflicts
          clearAllFilters();
          
          try {
            // Show loading on all product grids
            const productGrids = document.querySelectorAll('.product-grid');
            productGrids.forEach(grid => {
              grid.style.opacity = '0.5';
            });
            
            // Fetch sorted collection page
            const url = new URL(window.location.href);
            url.searchParams.set('sort_by', sortValue);
            url.searchParams.delete('page');
            
            const response = await fetch(url.toString());
            const html = await response.text();
            
            // Parse and extract new product grids
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newProductGrids = doc.querySelectorAll('.product-grid');
            const currentProductGrids = document.querySelectorAll('.product-grid');
            
            if (newProductGrids.length > 0 && currentProductGrids.length > 0) {
              // Replace ALL product grids (desktop, mobile, drawer duplicates)
              currentProductGrids.forEach((currentGrid, index) => {
                const newGrid = newProductGrids[index];
                
                if (newGrid) {
                  // Simply replace entire grid innerHTML
                  currentGrid.innerHTML = newGrid.innerHTML;
                }
              });
              
              // Re-fetch product data with sorting
              const dataUrl = `/collections/${collectionHandle}?view=output&sort_by=${sortValue}`;
              const dataResponse = await fetch(dataUrl);
              const newData = await dataResponse.json();
              
              
              // Update productData
              productData = {};
              newData.products.forEach(product => {
                productData[product.id] = {
                  collections: product.collections,
                  available: product.available
                };
              });
              
              
              // Reapply active filters
              setTimeout(() => {
                if (dataLoaded) {
                  filterProducts();
                }
              }, 50);
              
              // Hide loading on all grids
              productGrids.forEach(grid => {
                grid.style.opacity = '1';
              });
              
            }
            
            // Update URL
            window.history.pushState({}, '', url.toString());
            
          } catch (error) {
            console.error('[SORTING] Error during sorting:', error);
            // Hide loading on error
            const productGrids = document.querySelectorAll('.product-grid');
            productGrids.forEach(grid => {
              grid.style.opacity = '1';
            });
          }
        });
        
        select.dataset.customSortingAdded = 'true';
      });
    }
    
    // Initialize sorting
    initSorting();
    
    // Disable Shopify's native filtering by removing name attributes
    const customCheckboxes = document.querySelectorAll('.checkbox__input.custom-collection-checkbox, .checkbox__input.custom-availability-checkbox');
    customCheckboxes.forEach(cb => {
      if (cb.name) {
        cb.removeAttribute('name');
      }
    });
    
    // Handle clicks on custom checkboxes
    document.addEventListener('click', (e) => {
      if (e.target.matches('.checkbox__input.custom-collection-checkbox, .checkbox__input.custom-availability-checkbox')) {
        const checkboxId = e.target.id;
        const isFirstClick = !firstClickDone.has(checkboxId);
        
        if (!dataLoaded) {
          e.preventDefault();
          return;
        }
        
        // Fix for mysteriously checked state on first click
        if (isFirstClick && e.target.checked === true) {
          e.target.checked = true;
          setTimeout(() => { e.target.checked = false; }, 0);
          setTimeout(() => { e.target.checked = true; }, 10);
          firstClickDone.add(checkboxId);
          
          setTimeout(() => {
            filterProducts();
          }, 20);
        } else {
          firstClickDone.add(checkboxId);
          setTimeout(() => {
            filterProducts();
          }, 0);
        }
      }
      
      if (e.target.matches(`#custom-clear-all-btn-${sectionId}, #custom-mobile-clear-all-btn-${sectionId}`)) {
        clearAllFilters();
      }
    });
  }
}